import { isNationId, isPlayerColour } from '@brig/nations';
import { GAME_SIZE_IDS, RULES_VERSION, type Action, type GameSetup, type MapDef } from '@brig/rules-engine';
import { z } from 'zod';
import { DEFAULT_COLOURS, DEFAULT_NATIONS, type Colours, type History, type Nations } from '../history.ts';

export const LOG_FORMAT = 'brig-sandbox-log';
export const LOG_VERSION = 1;

/** The exported file: everything needed to replay the game deterministically (R-RNG-02). */
export interface SandboxLog {
  readonly format: typeof LOG_FORMAT;
  readonly version: typeof LOG_VERSION;
  readonly rulesVersion: string;
  readonly setup: GameSetup;
  /** Optional in files from before nations existed. */
  readonly nations: Nations;
  /** Optional in files from before player colours existed. */
  readonly colours: Colours;
  readonly actions: readonly Action[];
}

export function toLog(history: History): SandboxLog {
  return {
    format: LOG_FORMAT,
    version: LOG_VERSION,
    rulesVersion: RULES_VERSION,
    setup: history.setup,
    nations: history.nations,
    colours: history.colours,
    actions: history.actions,
  };
}

const player = z.object({ id: z.string(), name: z.string() });

// Structural check only; the engine validates the map (R-MAP-*) and every action on replay.
const logSchema = z.object({
  format: z.literal(LOG_FORMAT),
  version: z.literal(LOG_VERSION),
  rulesVersion: z.string(),
  setup: z.object({
    /** Absent in logs from before game sizes: those replay on the map they carry. */
    size: z.enum(GAME_SIZE_IDS).optional(),
    map: z.looseObject({ id: z.string(), rows: z.array(z.string()) }),
    ruleset: z.record(z.string(), z.unknown()),
    seed: z.number().int(),
    players: z.object({ red: player, blue: player }),
    options: z.record(z.string(), z.unknown()).optional(),
  }),
  nations: z
    .object({
      red: z.string().refine(isNationId, 'unknown nation'),
      blue: z.string().refine(isNationId, 'unknown nation'),
    })
    .refine((n) => n.red !== n.blue, 'the two sides must play different nations')
    .optional(),
  colours: z
    .object({
      red: z.string().refine(isPlayerColour, 'unknown colour'),
      blue: z.string().refine(isPlayerColour, 'unknown colour'),
    })
    .refine((c) => c.red !== c.blue, 'the two sides must fly different colours')
    .optional(),
  actions: z.array(z.looseObject({ type: z.string(), side: z.enum(['red', 'blue']) })),
});

/** Parses an exported log. Throws with a readable message on malformed input. */
export function parseLog(text: string): SandboxLog {
  let json: unknown;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error('The file is not valid JSON.');
  }
  const result = logSchema.safeParse(json);
  if (!result.success) {
    const issue = result.error.issues[0];
    throw new Error(`Not a sandbox log: ${issue ? `${issue.path.join('.')}: ${issue.message}` : 'invalid'}`);
  }
  const { setup, actions, rulesVersion, nations, colours } = result.data;
  return {
    format: LOG_FORMAT,
    version: LOG_VERSION,
    rulesVersion,
    setup: {
      ...(setup.size ? { size: setup.size } : {}),
      map: setup.map as unknown as MapDef,
      ruleset: setup.ruleset,
      seed: setup.seed,
      players: setup.players,
      ...(setup.options ? { options: setup.options } : {}),
    },
    nations: nations ?? DEFAULT_NATIONS,
    colours: colours ?? DEFAULT_COLOURS,
    actions: actions as unknown as Action[],
  };
}

export function logFileName(history: History): string {
  return `brig-sandbox-seed${history.setup.seed}-${history.actions.length}actions.json`;
}
